/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Schedule Executions Screen
 *
 * A dedicated screen for viewing all agent schedule executions
 * with filtering, statistics, and detailed execution views.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Collapse,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as ScheduleIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  PlayArrow as RunningIcon,
  SkipNext as SkippedIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as TimeIcon,
  Functions as FunctionsIcon,
  Toll as CreditIcon,
  Stop as StopIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scheduleExecutionApi, agentScheduleApi } from '../../services/api';
import { useAiDateTimeFormatting } from '../../hooks/useAiDateTimeFormatting';
import type {
  AgentScheduleExecutionListItem,
  AgentScheduleExecutionFilters,
  AgentScheduleExecutionStatus,
  AgentScheduleExecutionStats,
} from '../../types/agent';

// Status configurations
const statusConfig: Record<
  AgentScheduleExecutionStatus,
  { icon: React.ReactNode; color: 'success' | 'error' | 'warning' | 'info' | 'default'; label: string }
> = {
  SUCCESS: { icon: <SuccessIcon fontSize="small" />, color: 'success', label: 'Success' },
  ERROR: { icon: <ErrorIcon fontSize="small" />, color: 'error', label: 'Error' },
  PENDING: { icon: <PendingIcon fontSize="small" />, color: 'warning', label: 'Pending' },
  RUNNING: { icon: <RunningIcon fontSize="small" />, color: 'info', label: 'Running' },
  SKIPPED: { icon: <SkippedIcon fontSize="small" />, color: 'default', label: 'Skipped' },
};

const formatCredits = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(Number(value))) {
    return '-';
  }
  return `${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })} cr`;
};

const getScheduleExecutionCredits = (
  execution: Pick<AgentScheduleExecutionListItem, 'credit_usage' | 'credits_used'> | null | undefined
): number | null => {
  if (!execution) {
    return null;
  }
  const value = execution.credit_usage ?? execution.credits_used;
  return value == null || !Number.isFinite(Number(value)) ? null : Number(value);
};

// Statistics Card Component
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color = '#1976d2', subtitle }) => (
  <Card elevation={2}>
    <CardContent sx={{ py: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

// Execution Detail Dialog
interface ExecutionDetailDialogProps {
  open: boolean;
  onClose: () => void;
  execution: AgentScheduleExecutionListItem | null;
  onStop?: (executionId: string) => void;
  isStopping?: boolean;
}

const ExecutionDetailDialog: React.FC<ExecutionDetailDialogProps> = ({
  open,
  onClose,
  execution,
  onStop,
  isStopping,
}) => {
  const theme = useTheme();
  const { formatDateTime } = useAiDateTimeFormatting();
  const isDark = theme.palette.mode === 'dark';
  const [showFullResponse, setShowFullResponse] = useState(false);
  const [showFullTaskPrompt, setShowFullTaskPrompt] = useState(false);
  const [toolCallsExpanded, setToolCallsExpanded] = useState(false);

  const { data: executionDetail, isLoading: loadingDetail } = useQuery({
    queryKey: ['schedule-execution-detail', execution?.id],
    queryFn: () => (execution ? scheduleExecutionApi.get(execution.id) : null),
    enabled: open && !!execution,
    staleTime: 30000,
  });

  // Fetch full response when requested
  const { data: fullResponse, isLoading: loadingFull } = useQuery({
    queryKey: ['schedule-execution-full', execution?.id],
    queryFn: () => (execution ? scheduleExecutionApi.getFullResponse(execution.id) : null),
    enabled: open && showFullResponse && !!execution,
  });

  // Fetch tool calls when expanded
  const { data: toolCallsData, isLoading: loadingToolCalls } = useQuery({
    queryKey: ['schedule-execution-tool-calls', execution?.id],
    queryFn: () => (execution ? scheduleExecutionApi.getToolCalls(execution.id) : null),
    enabled: open && toolCallsExpanded && !!execution && (execution.tool_calls_count || 0) > 0,
  });

  const activeExecution = executionDetail || execution;

  if (!activeExecution) return null;

  const config = statusConfig[activeExecution.status];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ScheduleIcon color="primary" />
          Execution Details
          <Chip
            icon={config.icon as React.ReactElement}
            label={config.label}
            size="small"
            color={config.color}
            sx={{ ml: 2 }}
          />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loadingDetail && (
          <LinearProgress sx={{ mb: 2 }} />
        )}
        <Grid container spacing={2}>
          {/* Basic Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Schedule
            </Typography>
            <Typography variant="body1" gutterBottom>
              {activeExecution.schedule_name}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Agent
            </Typography>
            <Typography variant="body1" gutterBottom>
              {activeExecution.agent_name}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              AI System
            </Typography>
            <Typography variant="body1" gutterBottom>
              {activeExecution.ai_system_name || 'N/A'}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Duration
            </Typography>
            <Typography variant="body1" gutterBottom>
              {activeExecution.duration_seconds
                ? `${activeExecution.duration_seconds.toFixed(1)}s`
                : 'N/A'}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Timing */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Started At
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDateTime(activeExecution.started_at, undefined, 'Not started')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Completed At
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDateTime(activeExecution.completed_at, undefined, 'Not completed')}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Metrics */}
          <Grid size={{ xs: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Tool Calls
            </Typography>
            <Typography variant="body1" gutterBottom>
              {activeExecution.tool_calls_count}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Tokens Used
            </Typography>
            <Typography variant="body1" gutterBottom>
              {(Number(activeExecution.tokens_used) || 0).toLocaleString()}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Credit Usage
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatCredits(getScheduleExecutionCredits(activeExecution))}
            </Typography>
          </Grid>

          {/* Tool Calls Details */}
          {(activeExecution.tool_calls_count || 0) > 0 && (
            <Grid size={{ xs: 12 }}>
              <Accordion
                expanded={toolCallsExpanded}
                onChange={() => setToolCallsExpanded(!toolCallsExpanded)}
                sx={{ mt: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <BuildIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2">
                      Tool Calls ({activeExecution.tool_calls_count})
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {loadingToolCalls ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : toolCallsData?.tool_calls?.length ? (
                    <Stack spacing={2}>
                      {toolCallsData.tool_calls.map((tc, index) => (
                        <Paper
                          key={index}
                          variant="outlined"
                          sx={{ p: 1.5, bgcolor: isDark ? 'grey.900' : 'grey.50' }}
                        >
                          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="subtitle2" color="primary">
                              {tc.tool_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {tc.duration_ms}ms • {tc.tokens_used} tokens
                            </Typography>
                          </Box>
                          {tc.tool_input && Object.keys(tc.tool_input).length > 0 && (
                            <Box mb={1}>
                              <Typography variant="caption" color="text.secondary">
                                Input:
                              </Typography>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1,
                                  bgcolor: 'background.paper',
                                  maxHeight: 150,
                                  overflow: 'auto',
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  component="pre"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    m: 0,
                                  }}
                                >
                                  {JSON.stringify(tc.tool_input, null, 2)}
                                </Typography>
                              </Paper>
                            </Box>
                          )}
                          {tc.tool_output && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Output:
                              </Typography>
                              <Paper
                                variant="outlined"
                                sx={{
                                  p: 1,
                                  bgcolor: 'background.paper',
                                  maxHeight: 200,
                                  overflow: 'auto',
                                }}
                              >
                                {/* Check for image in output */}
                                {tc.tool_output?.data?._is_file_attachment &&
                                tc.tool_output?.data?.base64 &&
                                tc.tool_output?.data?.mime_type?.startsWith('image/') ? (
                                  <Box>
                                    <img
                                      src={`data:${tc.tool_output.data.mime_type};base64,${tc.tool_output.data.base64}`}
                                      alt={tc.tool_output.data.filename || 'Tool output image'}
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '300px',
                                        borderRadius: '4px',
                                      }}
                                    />
                                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                                      {tc.tool_output.data.filename}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    component="pre"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                      m: 0,
                                    }}
                                  >
                                    {JSON.stringify(tc.tool_output, null, 2)}
                                  </Typography>
                                )}
                              </Paper>
                            </Box>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      No tool call details available
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Task Prompt */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" color="text.secondary">
                Task Prompt
              </Typography>
              {activeExecution.task_prompt && activeExecution.task_prompt.length > 200 && (
                <Button
                  size="small"
                  onClick={() => setShowFullTaskPrompt(!showFullTaskPrompt)}
                  endIcon={showFullTaskPrompt ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {showFullTaskPrompt ? 'Collapse' : 'Expand'}
                </Button>
              )}
            </Box>
            <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: isDark ? 'grey.900' : 'grey.50', maxHeight: showFullTaskPrompt ? 400 : 100, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {showFullTaskPrompt || !activeExecution.task_prompt || activeExecution.task_prompt.length <= 200
                  ? activeExecution.task_prompt || activeExecution.task_prompt_preview
                  : activeExecution.task_prompt_preview}
              </Typography>
            </Paper>
          </Grid>

          {/* Response */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2" color="text.secondary">
                Response
              </Typography>
              {!showFullResponse && activeExecution.response?.length > 500 && (
                <Button
                  size="small"
                  onClick={() => setShowFullResponse(true)}
                  disabled={loadingFull}
                >
                  {loadingFull ? 'Loading...' : 'Show Full Response'}
                </Button>
              )}
            </Box>
            <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: isDark ? 'grey.900' : 'grey.50', maxHeight: 300, overflow: 'auto' }}>
              {loadingFull ? (
                <CircularProgress size={20} />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}
                >
                  {showFullResponse && fullResponse
                    ? fullResponse.response
                    : activeExecution.response || 'No response'}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Error */}
          {activeExecution.error && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" color="error">
                Error
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'error.lighter' }}>
                <Typography
                  variant="body2"
                  color="error"
                  sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                >
                  {activeExecution.error}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {activeExecution.status === 'RUNNING' && onStop && (
          <Button
            onClick={() => onStop(activeExecution.id)}
            color="error"
            variant="contained"
            disabled={isStopping}
            startIcon={isStopping ? <CircularProgress size={16} color="inherit" /> : <ErrorIcon />}
          >
            {isStopping ? 'Stopping...' : 'Stop Execution'}
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

interface ScheduleExecutionsScreenProps {
  aiSystemId?: number;
  showStatsInitially?: boolean;
}

// Main Component
export const ScheduleExecutionsScreen: React.FC<ScheduleExecutionsScreenProps> = ({
  aiSystemId,
  showStatsInitially = true,
}) => {
  const theme = useTheme();
  const { formatDateTime } = useAiDateTimeFormatting();
  const isDark = theme.palette.mode === 'dark';
  
  // Filters state
  const [filters, setFilters] = useState<AgentScheduleExecutionFilters>({
    ai_system_id: aiSystemId,
    page: 1,
    page_size: 20,
  });

  // UI state
  const [selectedExecution, setSelectedExecution] = useState<AgentScheduleExecutionListItem | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [showStats, setShowStats] = useState(showStatsInitially);
  const [statsDays, setStatsDays] = useState(7);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      ai_system_id: aiSystemId,
      page: 1,
    }));
  }, [aiSystemId]);

  // Fetch executions
  const {
    data: executionsData,
    isLoading: loadingExecutions,
    isFetching: fetchingExecutions,
    error: executionsError,
    refetch: refetchExecutions,
  } = useQuery({
    queryKey: ['schedule-executions', filters],
    queryFn: () => scheduleExecutionApi.list(filters),
    staleTime: 30000,
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    refetchInterval: (query) => {
      const data = query.state.data as any;
      const hasActiveExecution = data?.results?.some((execution: AgentScheduleExecutionListItem) =>
        execution.status === 'RUNNING' || execution.status === 'PENDING'
      );
      return hasActiveExecution ? 10000 : false;
    },
  });

  // Fetch statistics
  const {
    data: statsData,
    isLoading: loadingStats,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['schedule-execution-stats', statsDays, aiSystemId],
    queryFn: () => scheduleExecutionApi.getStats(statsDays, { ai_system_id: aiSystemId }),
    enabled: showStats,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  // Fetch schedules for filter dropdown
  const { data: schedulesData } = useQuery({
    queryKey: ['agent-schedules-for-filter', aiSystemId],
    queryFn: () => agentScheduleApi.listOptions({ ai_system_id: aiSystemId }),
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Stop execution mutation
  const stopMutation = useMutation({
    mutationFn: (executionId: string) => scheduleExecutionApi.stop(executionId),
    onSuccess: (data) => {
      // Refetch executions to update the list
      refetchExecutions();
      refetchStats();
      // Close dialog if it was successful
      if (data.success) {
        setDetailDialogOpen(false);
      }
    },
  });

  const handleStopExecution = (executionId: string) => {
    stopMutation.mutate(executionId);
  };

  const handleFilterChange = (key: keyof AgentScheduleExecutionFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1, // Reset to first page on filter change
    }));
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({
      ...prev,
      page_size: parseInt(event.target.value, 10),
      page: 1,
    }));
  };

  const handleViewExecution = (execution: AgentScheduleExecutionListItem) => {
    setSelectedExecution(execution);
    setDetailDialogOpen(true);
  };

  const handleRefresh = () => {
    refetchExecutions();
    if (showStats) {
      refetchStats();
    }
  };

  // Calculate success rate
  const successRate = useMemo(() => {
    if (!statsData || statsData.total_executions === 0) return 0;
    const successCount = statsData.by_status?.SUCCESS || 0;
    return (successCount / statsData.total_executions) * 100;
  }, [statsData]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Schedule Executions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor all agent schedule executions and their results
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <IconButton onClick={() => setShowStats(!showStats)}>
            {showStats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <span>
            <IconButton onClick={handleRefresh} disabled={loadingExecutions || fetchingExecutions}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Box>
      </Box>

      {/* Statistics Section */}
      <Collapse in={showStats}>
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight="medium">
              Statistics
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={statsDays}
                label="Period"
                onChange={(e) => setStatsDays(e.target.value as number)}
              >
                <MenuItem value={1}>Last 24h</MenuItem>
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={14}>Last 14 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {loadingStats ? (
            <LinearProgress sx={{ mb: 2 }} />
          ) : statsData ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Executions"
                  value={statsData.total_executions}
                  icon={<ScheduleIcon fontSize="large" />}
                  subtitle={`Last ${statsDays} days`}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Success Rate"
                  value={`${successRate.toFixed(1)}%`}
                  icon={<TrendingUpIcon fontSize="large" />}
                  color={successRate >= 80 ? '#2e7d32' : successRate >= 50 ? '#ed6c02' : '#d32f2f'}
                  subtitle={`${statsData.by_status?.SUCCESS || 0} successful`}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Tool Calls"
                  value={statsData.total_tool_calls.toLocaleString()}
                  icon={<FunctionsIcon fontSize="large" />}
                  subtitle={`Avg ${statsData.avg_tool_calls.toFixed(1)} per execution`}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Credits"
                  value={formatCredits(statsData.credit_usage ?? statsData.credits_used ?? 0)}
                  icon={<CreditIcon fontSize="large" />}
                  subtitle={`${statsData.total_tokens.toLocaleString()} tokens`}
                />
              </Grid>
            </Grid>
          ) : null}

          {/* Top Schedules */}
          {statsData?.top_schedules && statsData.top_schedules.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Top Schedules by Executions
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {statsData.top_schedules.map((schedule) => (
                  <Chip
                    key={schedule.id}
                    label={`${schedule.name} (${schedule.count})`}
                    size="small"
                    variant="outlined"
                    onClick={() => handleFilterChange('schedule_id', schedule.id)}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Collapse>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Schedule"
              value={filters.schedule_id || ''}
              onChange={(e) => handleFilterChange('schedule_id', e.target.value)}
            >
              <MenuItem value="">All Schedules</MenuItem>
              {schedulesData?.results.map((schedule) => (
                <MenuItem key={schedule.id} value={schedule.id}>
                  {schedule.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filters.status || ''}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {config.icon}
                    {config.label}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="From"
              value={filters.date_from || ''}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="To"
              value={filters.date_to || ''}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                setFilters({
                  ai_system_id: aiSystemId,
                  page: 1,
                  page_size: filters.page_size,
                })
              }
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {executionsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load executions: {(executionsError as Error).message}
        </Alert>
      )}

      {/* Executions Table */}
      <Paper>
        {(loadingExecutions || fetchingExecutions) && <LinearProgress />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>AI System</TableCell>
                <TableCell>Started</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell align="right">Tools</TableCell>
                <TableCell align="right">Tokens</TableCell>
                <TableCell align="right">Credits</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {executionsData?.results.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No executions found matching your filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                executionsData?.results.map((execution) => {
                  const config = statusConfig[execution.status];
                  return (
                    <TableRow
                      key={execution.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleViewExecution(execution)}
                    >
                      <TableCell>
                        <Chip
                          icon={config.icon as React.ReactElement}
                          label={config.label}
                          size="small"
                          color={config.color}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {execution.schedule_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {execution.agent_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {execution.ai_system_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(execution.started_at, undefined, '-')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {execution.duration_seconds
                            ? `${execution.duration_seconds.toFixed(1)}s`
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {execution.tool_calls_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {(Number(execution.tokens_used) || 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCredits(getScheduleExecutionCredits(execution))}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          {execution.status === 'RUNNING' && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStopExecution(execution.id);
                              }}
                              disabled={stopMutation.isPending}
                            >
                              <StopIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewExecution(execution);
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={executionsData?.count || 0}
          page={(filters.page || 1) - 1}
          onPageChange={handlePageChange}
          rowsPerPage={filters.page_size || 20}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      {/* Execution Detail Dialog */}
      <ExecutionDetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        execution={selectedExecution}
        onStop={handleStopExecution}
        isStopping={stopMutation.isPending}
      />
    </Box>
  );
};

export default ScheduleExecutionsScreen;
